import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TableSortLabel, TextField, Button, Alert,
  TablePagination, InputAdornment, Dialog, DialogTitle, DialogContent,
  DialogActions, Chip, Grid, Card, CardContent, IconButton,
  Menu, MenuItem, ListItemIcon, ListItemText, Accordion,
  AccordionSummary, AccordionDetails, CircularProgress, Tabs, Tab
} from '@mui/material';
import {
  Search as SearchIcon,
  MoreVert as MoreVertIcon,
  Visibility as VisibilityIcon,
  Download as DownloadIcon,
  ExpandMore as ExpandMoreIcon,
  FilterList as FilterListIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import API_CONFIG, { getApiUrl } from '../../config/api';

const ResponsesManagement = () => {
  const [responses, setResponses] = useState([]);
  const [surveys, setSurveys] = useState([]);
  const [questions, setQuestions] = useState({});
  const [order, setOrder] = useState('desc');
  const [orderBy, setOrderBy] = useState('created_at');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState('');
  const [selectedResponse, setSelectedResponse] = useState(null);
  const [responseDialogOpen, setResponseDialogOpen] = useState(false);
  const [loadingResponses, setLoadingResponses] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuResponse, setMenuResponse] = useState(null);
  const [selectedSurveyId, setSelectedSurveyId] = useState('');
  const [tabValue, setTabValue] = useState(0);

  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const fetchAllResponses = async () => {
    setLoading(true);
    setError('');
    try {
      // Get all users first
      const usersRes = await fetch(getApiUrl(API_CONFIG.ENDPOINTS.USER.LIST_USERS_WITH_ONBOARDING), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ admin_password: 'Admin@123!!!' })
      });
      const usersData = await usersRes.json();

      if (usersData.error) throw new Error(usersData.error);

      const allResponses = [];
      const allSurveys = [];
      const allQuestions = {};

      // Fetch responses for each user's surveys
      for (const user of usersData.users || []) {
        try {
          const surveysRes = await fetch(getApiUrl(API_CONFIG.ENDPOINTS.SURVEYS.LIST) + `?user_id=${user.id}`);
          const surveysData = await surveysRes.json();

          if (surveysData.status === 'success' && surveysData.surveys) {
            const userSurveys = surveysData.surveys.map(survey => ({
              ...survey,
              user_id: user.id,
              user_email: user.email,
              user_name: user.display_name
            }));
            allSurveys.push(...userSurveys);

            // Fetch responses for each survey
            for (const survey of userSurveys) {
              try {
                const responsesRes = await fetch(getApiUrl('/get_all_survey_responses.php') + `?survey_id=${survey.id}&status=completed`);
                const responsesData = await responsesRes.json();

                if (responsesData.status === 'success' && responsesData.data) {
                  const surveyResponses = responsesData.data.responses.map(response => ({
                    ...response,
                    survey_id: survey.id,
                    survey_name: survey.name,
                    user_id: user.id,
                    user_email: user.email,
                    user_name: user.display_name
                  }));
                  allResponses.push(...surveyResponses);

                  // Store questions for this survey
                  if (responsesData.data.questions) {
                    allQuestions[survey.id] = responsesData.data.questions;
                  }
                }
              } catch (e) {
                console.error('Error fetching responses for survey:', survey.id, e);
              }
            }
          }
        } catch (e) {
          console.error('Error fetching surveys for user:', user.id, e);
        }
      }

      setResponses(allResponses);
      setSurveys(allSurveys);
      setQuestions(allQuestions);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchSurveyResponses = async (surveyId) => {
    setLoadingResponses(true);
    setError('');
    try {
      const responsesRes = await fetch(getApiUrl('/get_all_survey_responses.php') + `?survey_id=${surveyId}&status=completed`);
      const responsesData = await responsesRes.json();

      if (responsesData.status === 'success' && responsesData.data) {
        const surveyResponses = responsesData.data.responses.map(response => ({
          ...response,
          survey_id: surveyId,
          survey_name: surveys.find(s => s.id === surveyId)?.name || 'Unknown Survey'
        }));
        setResponses(surveyResponses);

        // Store questions for this survey
        if (responsesData.data.questions) {
          setQuestions(prev => ({ ...prev, [surveyId]: responsesData.data.questions }));
        }
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoadingResponses(false);
    }
  };

  const handleSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleMenuClick = (event, response) => {
    setAnchorEl(event.currentTarget);
    setMenuResponse(response);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuResponse(null);
  };

  const handleViewResponse = (response) => {
    setSelectedResponse(response);
    setResponseDialogOpen(true);
    handleMenuClose();
  };

  const handleExportResponse = (response) => {
    // Implement response export
    console.log('Export response:', response);
    handleMenuClose();
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    if (newValue === 0) {
      // All responses
      fetchAllResponses();
    } else if (newValue === 1 && selectedSurveyId) {
      // Survey-specific responses
      fetchSurveyResponses(selectedSurveyId);
    }
  };

  const handleSurveyFilter = (surveyId) => {
    setSelectedSurveyId(surveyId);
    setTabValue(1);
    fetchSurveyResponses(surveyId);
  };

  useEffect(() => {
    const surveyId = searchParams.get('survey_id');
    if (surveyId) {
      setSelectedSurveyId(surveyId);
      setTabValue(1);
      fetchSurveyResponses(surveyId);
    } else {
      fetchAllResponses();
    }
  }, []);

  // Global search filter
  const filteredResponses = responses.filter(response => {
    if (!search) return true;
    const searchStr = search.toLowerCase();
    return (
      response.survey_name.toLowerCase().includes(searchStr) ||
      response.user_email.toLowerCase().includes(searchStr) ||
      response.respondent_email.toLowerCase().includes(searchStr)
    );
  });

  // Sort responses
  const sortedResponses = [...filteredResponses].sort((a, b) => {
    let aValue, bValue;

    switch (orderBy) {
      case 'survey_name':
        aValue = a.survey_name || '';
        bValue = b.survey_name || '';
        break;
      case 'respondent_email':
        aValue = a.respondent_email || '';
        bValue = b.respondent_email || '';
        break;
      case 'created_at':
      default:
        aValue = new Date(a.created_at);
        bValue = new Date(b.created_at);
        break;
    }

    if (order === 'asc') {
      return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
    } else {
      return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
    }
  });

  return (
    <Box sx={{ p: 0 }}>
      <Typography variant="h4" gutterBottom sx={{ mb: 4 }}>
        Responses Management
      </Typography>

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={tabValue} onChange={handleTabChange} aria-label="response tabs">
          <Tab label="All Responses" />
          <Tab label="Survey Responses" disabled={!selectedSurveyId} />
        </Tabs>
      </Paper>

      {/* Filters */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              placeholder="Search responses..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                )
              }}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={() => tabValue === 0 ? fetchAllResponses() : fetchSurveyResponses(selectedSurveyId)}
              fullWidth
            >
              Refresh
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Responses Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'survey_name'}
                  direction={orderBy === 'survey_name' ? order : 'asc'}
                  onClick={() => handleSort('survey_name')}
                >
                  Survey
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'respondent_email'}
                  direction={orderBy === 'respondent_email' ? order : 'asc'}
                  onClick={() => handleSort('respondent_email')}
                >
                  Respondent
                </TableSortLabel>
              </TableCell>
              <TableCell>Owner</TableCell>
              <TableCell>Response Count</TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'created_at'}
                  direction={orderBy === 'created_at' ? order : 'asc'}
                  onClick={() => handleSort('created_at')}
                >
                  Submitted
                </TableSortLabel>
              </TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedResponses.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map(response => (
              <TableRow key={response.id} hover>
                <TableCell>
                  <Typography variant="body2" fontWeight="medium">
                    {response.survey_name}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    ID: {response.survey_id}
                  </Typography>
                </TableCell>
                <TableCell>{response.respondent_email}</TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {response.user_name || 'N/A'}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {response.user_email}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={`${response.responses?.length || 0} answers`}
                    size="small"
                    variant="outlined"
                  />
                </TableCell>
                <TableCell>
                  {response.created_at ? new Date(response.created_at).toLocaleDateString() : 'N/A'}
                </TableCell>
                <TableCell>
                  <IconButton onClick={(e) => handleMenuClick(e, response)}>
                    <MoreVertIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={sortedResponses.length}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={e => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[5, 10, 25, 50]}
        />
      </TableContainer>

      {/* Response Details Dialog */}
      <Dialog open={responseDialogOpen} onClose={() => setResponseDialogOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>
          Response Details - {selectedResponse?.respondent_email}
        </DialogTitle>
        <DialogContent>
          {selectedResponse && (
            <Grid container spacing={3} sx={{ mt: 1 }}>
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>Response Information</Typography>
                    <Box sx={{ display: 'grid', gap: 1 }}>
                      <Typography><strong>Survey:</strong> {selectedResponse.survey_name}</Typography>
                      <Typography><strong>Respondent:</strong> {selectedResponse.respondent_email}</Typography>
                      <Typography><strong>Owner:</strong> {selectedResponse.user_name} ({selectedResponse.user_email})</Typography>
                      <Typography><strong>Submitted:</strong> {selectedResponse.created_at ? new Date(selectedResponse.created_at).toLocaleString() : 'N/A'}</Typography>
                      <Typography><strong>Browser:</strong> {selectedResponse.browser || 'N/A'}</Typography>
                      <Typography><strong>OS:</strong> {selectedResponse.os || 'N/A'}</Typography>
                      <Typography><strong>Location:</strong> {selectedResponse.location || 'N/A'}</Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>Answers ({selectedResponse.responses?.length || 0})</Typography>
                    {selectedResponse.responses?.length > 0 ? (
                      <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
                        {selectedResponse.responses.map((answer, index) => (
                          <Accordion key={index} sx={{ mb: 1 }}>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                              <Typography variant="body2">
                                {answer.questionText}
                              </Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                              <Typography variant="body2" sx={{ mb: 1 }}>
                                <strong>Answer:</strong> {answer.answer}
                              </Typography>
                              <Typography variant="caption" color="textSecondary">
                                Type: {answer.questionType} | Time spent: {answer.timeSpentSeconds || 0}s
                              </Typography>
                            </AccordionDetails>
                          </Accordion>
                        ))}
                      </Box>
                    ) : (
                      <Typography variant="body2" color="textSecondary">
                        No answers found
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResponseDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Response Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => handleViewResponse(menuResponse)}>
          <ListItemIcon>
            <VisibilityIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>View Details</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleExportResponse(menuResponse)}>
          <ListItemIcon>
            <DownloadIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Export Response</ListItemText>
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default ResponsesManagement;